import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Home,
  Upload,
  ShoppingBag,
  Boxes,
  Menu,
  Sliders,
  FileText,
  ShieldCheck,
  User,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import ConnectToEbayButton from "../common/ConnectToEbayButton";
import {
  DASHBOARD,
  UPLOAD as UPLOAD_ROUTE,
  EBAY,
  EBAY_SYNC,
  BATCHES,
  GLOBAL_PRESETS,
  TITLE_BUILDER,
  POLICIES,
  PROFILE_SETTINGS,
} from "../../routes/routes";

// Styled Components
const SidebarContainer = styled.aside`
  width: ${(props) => (props.$collapsed ? "80px" : "240px")};
  background: #001c49;
  // border-right: 1px solid #374151;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  z-index: 999;
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease;

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;
    top: 0;
    left: 0;
    right: 0;
    transform: ${(props) =>
      props.$isOpen ? "translateY(0)" : "translateY(-100%)"};
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  @media (max-width: 768px) {
    transform: ${(props) =>
      props.$isOpen ? "translateY(0)" : "translateY(-100%)"};
  }
`;

const SidebarContent = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;

  @media (max-width: 1024px) {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    padding: 1rem;
    gap: 0.5rem;
    align-items: center;
  }
`;

const NavigationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;

  @media (max-width: 1024px) {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const NavigationItem = styled.li`
  margin: 0;
  width: 100%;
  display: flex;
  justify-content: stretch;

  @media (max-width: 1024px) {
    width: auto;
    justify-content: center;
  }
`;

const NavigationLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$collapsed ? "center" : "flex-start")};
  padding: ${(props) => (props.$collapsed ? "0.75rem" : "0.75rem 1rem")};
  color: #9ca3af;
  text-decoration: none;
  transition: all 0.2s ease;
  border-radius: 8px;
  width: 100%;
  height: 48px;
  position: relative;
  gap: 0.75rem;
  margin: 2px 6px;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: rgba(59, 130, 246, 0.18);
    color: #f9fafb;
  }

  &:focus,
  &:active,
  &:focus-visible {
    outline: none;
    outline-offset: 0;
    box-shadow: none;
    border: none;
  }

  &.active {
    background-color: #ffffff1c;
    color: white;
    font-weight: 600;
    border-left: none;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.1),
      inset 0 -1px 0 rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @media (max-width: 1024px) {
    width: auto;
    height: auto;
    padding: 0.75rem 1.25rem;
    border-radius: 12px;
    justify-content: center;
    gap: 0.5rem;
    margin: 0.125rem;
  }
`;

const NavLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
  display: ${(props) => (props.$collapsed ? "none" : "inline")};

  @media (max-width: 1024px) {
    display: inline;
  }
`;

const Overlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;

  @media (max-width: 1024px) {
    display: ${(props) => (props.$isOpen ? "block" : "none")};
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$collapsed ? "center" : "space-between")};
  padding: 1.5rem 1rem;
  gap: 0.5rem;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  overflow: hidden;
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: bold;
  color: #000000;
`;

const LogoText = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  display: ${(props) => (props.$collapsed ? "none" : "block")};

  @media (max-width: 1024px) {
    display: block;
  }
`;

const MenuToggle = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  @media (min-width: 1025px) {
    display: none;
  }
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding: 0.5rem 0 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 1024px) {
    display: none;
  }
`;

const CollapseToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$collapsed ? "center" : "flex-start")};
  gap: 0.75rem;
  width: calc(100% - 12px);
  height: 44px;
  padding: ${(props) => (props.$collapsed ? "0.75rem" : "0.75rem 1rem")};
  margin: 2px 6px;
  background: none;
  border: none;
  border-radius: 8px;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(59, 130, 246, 0.18);
    color: #f9fafb;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const CollapseLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  white-space: nowrap;
  display: ${(props) => (props.$collapsed ? "none" : "inline")};
`;

function Sidebar({ isOpen, onToggle, collapsed = false, onToggleCollapse }) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => {
    if (path === BATCHES) {
      // Highlight "Batches" for all batch-related routes
      return (
        location.pathname === path ||
        location.pathname.startsWith("/batches/") ||
        location.pathname.includes("/batches/")
      );
    }
    if (path === EBAY_SYNC) {
      return (
        location.pathname === path ||
        location.pathname.startsWith("/ebay-sync/")
      );
    }
    return location.pathname === path;
  };

  const navigationItems = [
    {
      path: DASHBOARD,
      label: "Dashboard",
      icon: Home,
    },
    {
      path: BATCHES,
      label: "Batches",
      icon: Boxes,
    },
    {
      path: EBAY_SYNC,
      label: "eBay Sync",
      icon: ShoppingBag,
    },
    {
      path: UPLOAD_ROUTE,
      label: "Upload",
      icon: Upload,
    },
    {
      path: GLOBAL_PRESETS,
      label: "Global Presets",
      icon: Sliders,
    },
    {
      path: TITLE_BUILDER,
      label: "Default Title Format",
      icon: FileText,
    },
    {
      path: POLICIES,
      label: "Policies & Tax",
      icon: ShieldCheck,
    },
    {
      path: PROFILE_SETTINGS,
      label: "Profile Settings",
      icon: User,
    },
    // {
    //   path: EBAY,
    //   label: "eBay",
    //   icon: ShoppingBag,
    // },
  ];

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onToggle} />

      <SidebarContainer $isOpen={isOpen} $collapsed={collapsed}>
        {/* Logo Section */}
        <LogoSection $collapsed={collapsed}>
          <LogoContainer>
            <LogoIcon>SC</LogoIcon>
            <LogoText $collapsed={collapsed}>Sticker Checker</LogoText>
          </LogoContainer>
          <MenuToggle onClick={onToggle}>
            <Menu size={20} />
          </MenuToggle>
        </LogoSection>

        {/* Navigation Links */}
        <SidebarContent>
          <NavigationList>
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <NavigationItem key={item.path}>
                  <NavigationLink
                    to={item.path}
                    className={isActive(item.path) ? "active" : ""}
                    onClick={() => {
                      // Close sidebar on mobile/tablet when link is clicked
                      if (window.innerWidth <= 1024) {
                        onToggle();
                      }
                    }}
                    title={item.label}
                    $collapsed={collapsed}
                  >
                    <IconComponent size={20} />
                    <NavLabel $collapsed={collapsed}>{item.label}</NavLabel>
                  </NavigationLink>
                </NavigationItem>
              );
            })}
            <NavigationItem>
              <ConnectToEbayButton collapsed={collapsed} />
            </NavigationItem>
          </NavigationList>
        </SidebarContent>

        <SidebarFooter>
          <CollapseToggle
            onClick={onToggleCollapse}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            $collapsed={collapsed}
          >
            {collapsed ? (
              <PanelLeftOpen size={20} />
            ) : (
              <PanelLeftClose size={20} />
            )}
            <CollapseLabel $collapsed={collapsed}>Collapse</CollapseLabel>
          </CollapseToggle>
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
}

export default Sidebar;
